import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Col, Card, CardBody, CardHeader, Container, Row, Button } from 'reactstrap';
import axios from 'axios';
import Cookies from 'js-cookie';
import * as constants from 'constants/index.js';
import CardsHeader from 'layouts/Headers/CardsHeader.js';
import CustomSearch from '@pages/components/CustomSearch';
import { ButtonContainer } from '@pages/reusable-components/styled-components';
import TableCustom from 'views/pages/components/TableCustom';
import { fetchTrackingDevices } from '@pages/tracking/api/devicesApi';
import { InputKeySelect } from '../components/InputSelect';
import notify from '@utils/notify';
import VirtualizedTable from '@pages/components/VirtualizedTable';

export default function DevicesReport() {
  const history = useHistory();
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState(''); // holds full pagination object
  const [devices, setDevices] = useState([]); // current page data array
  const [memoDevices, setMemoDevices] = useState([]); // memoized device list
  const [filter, setFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  useEffect(() => {
    const grs_token = Cookies.get('grs_token');
    setToken(grs_token);
    const source = axios.CancelToken.source();
    fetchDevices(source, grs_token);
    return () => source.cancel();
  }, []);
console.log('Rendering DevicesReport with items:');
  const fetchDevices = async (source, tokenArg) => {
    setLoading(true);
    try {
      const itemsFetched = await fetchTrackingDevices({ token: tokenArg, source });
      setDevices(itemsFetched);
      setMemoDevices(itemsFetched); // memoize once after fetch
      setItems({ data: itemsFetched }); // mimic pagination for now
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (axios.isCancel(error)) return;
      console.error('Devices report fetch error :: ', error);
      notify.warning(
        error.response && error.response.data
          ? error.response.data.message
          : error.response
          ? error.response.statusText
          : 'Error Occurred!'
      );
    }
  };

  // Only filter when search button is clicked
  const filteredDevices = React.useMemo(() => {
    if (!activeSearch || activeSearch.length < 2) return memoDevices;
    const term = activeSearch.toLowerCase();
    return memoDevices.filter((d) => (d.name || '').toLowerCase().includes(term));
  }, [memoDevices, activeSearch]);

  const editTracking = (row) => {
    history.push({ pathname: `/admin/tracking/device/${row.uuid}` });
  };

  // Helper to check if device needs attention (not active in past 3 days)
  const needsAttention = (row) => {
    const ts = row.location?.timestamp;
    if (!ts) return true;
    const lastActive = new Date(ts);
    if (isNaN(lastActive.getTime())) return true;
    const now = new Date();
    const diffMs = now - lastActive;
    return diffMs > 3 * 24 * 60 * 60 * 1000;
  };

  return (
    <>
      <CardsHeader
        name="Tracking Management"
        parentName="Tracking Management"
        currentName="Devices Report"
      />
      <Container className="mt--5 admin-main-body" fluid>
        <Row>
          <Col xl="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between">
                  <div>
                    <h1 className="title">Devices Report</h1>
                    <span className="text-sm mb-0">Overview of all tracker devices.</span>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col lg={3}>
                    <CustomSearch
                      inputWidth={'100%'}
                      border={'1px solid #ccc'}
                      borderRadius={'8px'}
                      paddingTop={'7px'}
                      paddingBottom={'7px'}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search"
                    />
                  </Col>
                  <Col lg={2}>
                    <ButtonContainer
                      style={{ margin: 5 }}
                      reverse
                      onClick={() => setActiveSearch(searchTerm)}
                    >
                      Search
                    </ButtonContainer>
                  </Col>
                  <Col lg={2}>
                    <ButtonContainer
                      style={{ margin: 5 }}
                      onClick={() => { setSearchTerm(''); setActiveSearch(''); }}
                    >
                      Reset
                    </ButtonContainer>
                  </Col>
                </Row>
              </CardBody>
              <CardBody>
                <VirtualizedTable
                  items={filteredDevices}
                  columns={[
                    {
                      dataField: 'name',
                      text: 'Device name',
                      sort: true,
                    },
                    {
                      dataField: 'identifier',
                      text: 'Identifier',
                      sort: true,
                      formatter: (rowContent, row) => row.identifier || '-',
                    },
                    {
                      dataField: 'last_active',
                      text: 'Last Active',
                      sort: true,
                      formatter: (rowContent, row) => {
                        const ts = row.location?.timestamp;
                        if (!ts) return 'Never active';
                        const d = new Date(ts);
                        if (isNaN(d.getTime())) return '-';
                        const pad = (n) => n.toString().padStart(2, '0');
                        return `${pad(d.getDate())}/${pad(
                          d.getMonth() + 1
                        )}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
                          d.getSeconds()
                        )}`;
                      },
                    },
                  ]}
                  rowClassName={(row) => (needsAttention(row) ? 'bg-light-danger' : '')}
                  rowHeight={44}
                  height={500}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
// Add CSS for bg-light-danger in your stylesheet:
// .bg-light-danger { background-color: #ffe5e5 !important; }
